<template>
  <TransitionRoot as="template" :show="!!props.open">
    <Dialog as="div" class="relative z-backdrop-reset" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div
          class="flex min-h-full justify-center p-4 text-center items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative rounded-md bg-white text-left shadow-xl transition-all w-full sm:max-w-md"
            >
              <div class="flex flex-col">
                <div class="flex items-center gap-3 px-6 py-4 border-b">
                  <div class="border border-gray-200 p-2.5 rounded-xl">
                    <BuildingSeven
                      class="h-5 w-5 text-gray-900"
                      aria-hidden="true"
                    />
                  </div>
                  <h1 class="text-gray-900 text-lg font-semibold">
                    {{ t("admin.settings.organisation.add_building") }}
                  </h1>
                </div>
                <div class="flex flex-col gap-4 px-6 pt-4">
                  <Errors />

                  <DynamicInputComponent
                    v-for="field in fields"
                    :key="field.key"
                    :field="field"
                    :modelValue="form[field.key]"
                    @update:modelValue="onInput"
                    :form="form"
                  />
                </div>
              </div>
              <div class="flex flex-col-reverse sm:flex-row gap-3 p-6">
                <secondary-button
                  class="grow"
                  :label="t('components.buttons.cancel')"
                  @click="handleClose"
                />
                <primary-button
                  class="grow"
                  :loading="busy"
                  :disabled="busy"
                  :label="t('components.buttons.submit')"
                  @click="submit"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, defineProps } from "vue";
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import { useI18n } from "vue-i18n";
import { useFormSetup } from "@/Tailwind/Mixins/useFormSetup";
import DynamicInputComponent from "@/Tailwind/Components/Settings/DynamicInputComponent.vue";
import SecondaryButton from "@/Tailwind/Components/Buttons/SecondaryButton.vue";
import PrimaryButton from "@/Tailwind/Components/Buttons/PrimaryButton.vue";
import BuildingSeven from "@/Tailwind/Icon/BuildingSeven.vue";
import Errors from "@/Tailwind/Components/Errors.vue";
import { useAddBuilding } from "@/Composables/useSites";

const { mapDetailsToForm } = useFormSetup();
const { t } = useI18n();
const emit = defineEmits(["close"]);

const props = defineProps({
  open: Boolean,
});

const { isPending: busy, mutateAsync: addBuilding } = useAddBuilding();

const fields = computed(() => [
  {
    key: "name",
    title: t("validation.attributes.name"),
    type: "text",
  },
  {
    key: "customer_id",
    title: t("admin.settings.organisation.customer_name"),
    type: "dropdown",
    options: customerOptions,
    extraAttributes: {
      selectorStyle: "input",
    },
  },
]);

const customers = computed(() => usePage().props.customers);
const customerOptions = computed(() =>
  customers.value.map((customer) => ({
    label: customer.company_name,
    value: customer.id,
  }))
);

let form = mapDetailsToForm(fields.value);

const onInput = (field, value) => {
  form[field.key] = value;
};

function handleClose() {
  emit("close");
}

const submit = () => {
  addBuilding(form).then((newBuilding) => {
    router.visit(route("settings.location.index", newBuilding.id));
  });
};
</script>
